import { useState, useEffect, useRef } from 'react';
import { pendingAdmissionAPI, membersAPI } from '../../services/api';

function avatarColor(name = '') {
  const colors = ['bg-orange-500', 'bg-violet-600', 'bg-emerald-600', 'bg-rose-500', 'bg-indigo-600', 'bg-amber-500'];
  return colors[(name.charCodeAt(0) || 0) % colors.length];
}

export function AdmitModal({ record, onClose, onAdmitted }) {
  const [rollNo, setRollNo] = useState('');
  const [admissionDate, setAdmissionDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await pendingAdmissionAPI.admit(record.id, { roll_no: rollNo, admission_date: admissionDate || undefined });
      onAdmitted();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to admit member.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-semibold text-slate-800">Admit {record.full_name}</h2>
        <p className="mt-1 text-xs text-slate-400">
          Enter the roll number and the actual swearing-in date. This will create the member record.
        </p>
        {error && <p className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</p>}
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Roll Number</label>
            <input type="text" required value={rollNo} onChange={(e) => setRollNo(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Admission Date (swearing-in date)</label>
            <input type="date" required value={admissionDate} onChange={(e) => setAdmissionDate(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            <p className="mt-1 text-[11px] text-slate-400">Use the actual date they were sworn in — not today's date, unless that's the same day.</p>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-medium">
              {loading ? 'Admitting…' : 'Admit as Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function InviteToPayModal({ record, onClose, context = 'pending_admission' }) {
  const [email, setEmail] = useState(record.email || '');
  const [phone, setPhone] = useState(record.phone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await membersAPI.inviteById(record.member_id, { email, phone });
      setResult(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-semibold text-slate-800">
          {context === 'pending_admission' ? `Invite ${record.full_name} to Pay` : `Invite ${record.full_name} to Create Account`}
        </h2>
        <p className="mt-1 text-xs text-slate-400">
          {context === 'pending_admission'
            ? 'Sends a registration link so they can set a password and pay membership fees before their roll number is assigned.'
            : 'Sends a registration link so they can set a password and access their member portal.'}
        </p>
        {error && <p className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</p>}
        {result ? (
          <div className="mt-3 space-y-3">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-xs text-emerald-700">
              <p className="font-semibold">{result.message}</p>
              {result.invitation_url && <p className="mt-1 break-all">{result.invitation_url}</p>}
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onClose}
                className="rounded-xl bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 text-sm font-medium">
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Email (optional)</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              <p className="mt-1 text-[11px] text-slate-400">
                Leave blank if you only have their phone number — the invitation will be sent via SMS only,
                and they'll set their own email when they open the registration link.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
              <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={onClose}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-medium">
                {loading ? 'Sending…' : 'Send Invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function EditPendingAdmissionModal({ record, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: record.full_name || '',
    phone: record.phone || '',
    email: record.member_email || '',
    qualification: record.qualification || '',
    firm_name: record.firm_name || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await pendingAdmissionAPI.update(record.id, form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-semibold text-slate-800">Edit {record.full_name}</h2>
        <p className="mt-1 text-xs text-slate-400">
          Update contact and firm details for this pending admission.
        </p>
        {error && <p className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</p>}
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Full Name</label>
            <input type="text" name="full_name" value={form.full_name} onChange={handleChange} required
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
            <input type="tel" name="phone" value={form.phone} onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
            <input type="email" name="email" value={form.email} onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            {!record.member_id && (
              <p className="mt-1 text-[11px] text-slate-400">No provisional member profile linked yet — email will be saved once one exists.</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Firm / Institution</label>
            <input type="text" name="firm_name" value={form.firm_name} onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Qualification</label>
            <input type="text" name="qualification" value={form.qualification} onChange={handleChange}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-medium">
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function InviteAllConfirmModal({ eligibleCount, onClose, onStarted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleConfirm() {
    setLoading(true);
    setError('');
    try {
      const res = await pendingAdmissionAPI.inviteAll();
      onStarted(res.job_id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start the bulk invite job.');
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-semibold text-slate-800">Invite All to Pay</h2>
        <p className="mt-2 text-sm text-slate-600">
          This will send a registration + payment link by SMS to{' '}
          <span className="font-semibold text-slate-900">{eligibleCount.toLocaleString()} pending admission{eligibleCount === 1 ? '' : 's'}</span>{' '}
          who don't have an account yet. This cannot be undone once started.
        </p>
        <p className="mt-2 text-xs text-slate-400">
          It runs in the background so the portal stays fast for everyone else — you'll see live progress here.
        </p>
        {error && <p className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</p>}
        <div className="mt-4 flex gap-2 justify-end">
          <button type="button" onClick={onClose} disabled={loading}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={loading}
            className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-medium">
            {loading ? 'Starting…' : `Yes, Invite All ${eligibleCount.toLocaleString()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function InviteAllProgressModal({ jobId, onClose, onFinished }) {
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await pendingAdmissionAPI.jobStatus(jobId);
        if (cancelled) return;
        setJob(res.job);
        if (res.job && (res.job.status === 'completed' || res.job.status === 'failed')) {
          clearInterval(pollRef.current);
          onFinished?.();
        }
      } catch {
        // transient errors while polling are fine — try again next tick
      }
    }

    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [jobId]);

  const total = job?.total || 0;
  const processed = job?.processed || 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const isDone = job?.status === 'completed';
  const isRunning = job?.status === 'pending' || job?.status === 'running';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-semibold text-slate-800">
          {isDone ? 'Invite All — Complete' : 'Inviting All Pending Admissions…'}
        </h2>
        <p className="mt-1 text-xs text-slate-400">
          {isRunning
            ? job?.status === 'pending'
              ? 'Queued — the background worker picks this up within a minute.'
              : 'Sending invitations one by one in the background…'
            : 'All done.'}
        </p>

        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>{processed.toLocaleString()} / {total.toLocaleString()}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isDone ? 'bg-emerald-500' : 'bg-orange-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-center">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 py-2.5">
            <p className="text-lg font-bold text-emerald-700">{(job?.succeeded || 0).toLocaleString()}</p>
            <p className="text-[11px] text-emerald-600">Succeeded</p>
          </div>
          <div className="rounded-xl bg-red-50 border border-red-200 py-2.5">
            <p className="text-lg font-bold text-red-700">{(job?.failed || 0).toLocaleString()}</p>
            <p className="text-[11px] text-red-600">Failed / Skipped</p>
          </div>
        </div>

        {job?.log && (
          <div className="mt-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Recent activity</p>
            <div className="max-h-32 overflow-y-auto rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-500 font-mono whitespace-pre-line">
              {job.log.split('\n').slice(-8).join('\n')}
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
            {isDone ? 'Close' : 'Run in Background (Close)'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PendingAdmissions() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [accountTab, setAccountTab] = useState('none'); // 'has' | 'none'
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1, has_next: false, has_previous: false });
  const [admitTarget, setAdmitTarget] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  const [inviteAllConfirmOpen, setInviteAllConfirmOpen] = useState(false);
  const [inviteAllEligible, setInviteAllEligible] = useState(0);
  const [inviteAllChecking, setInviteAllChecking] = useState(false);
  const [inviteAllJobId, setInviteAllJobId] = useState(null);
  const [inviteAllNotice, setInviteAllNotice] = useState('');
  const fileInputRef = useRef(null);
  const searchTimer = useRef(null);

  // On page load, resume watching any bulk-invite job that's already in progress
  // (e.g. admin started it, navigated away, and came back).
  useEffect(() => {
    pendingAdmissionAPI.inviteAllCount()
      .then((res) => {
        if (res.active_job) setInviteAllJobId(res.active_job.id);
      })
      .catch(() => {});
  }, []);

  async function handleInviteAllClick() {
    setInviteAllChecking(true);
    setInviteAllNotice('');
    try {
      const res = await pendingAdmissionAPI.inviteAllCount();
      if (res.active_job) {
        setInviteAllJobId(res.active_job.id);
      } else if (res.eligible_count === 0) {
        setInviteAllNotice('No eligible pending admissions to invite — everyone already has an account or has no phone number on file.');
      } else {
        setInviteAllEligible(res.eligible_count);
        setInviteAllConfirmOpen(true);
      }
    } catch {
      setInviteAllNotice('Could not check eligible admissions. Please try again.');
    } finally {
      setInviteAllChecking(false);
    }
  }

  function load() {
    setLoading(true);
    const status = activeTab === 'all' ? '' : activeTab;
    const hasAccount = accountTab === 'has' ? '1' : '0';
    pendingAdmissionAPI.list(page, 20, search, status, hasAccount)
      .then((res) => {
        setRecords(res.pending_admissions || []);
        setPagination({
          page: res.page || 1,
          total: res.total || 0,
          total_pages: res.total_pages || 1,
          has_next: !!res.has_next,
          has_previous: !!res.has_previous,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(load, [page, search, activeTab, accountTab]);

  function handleSearch(e) {
    const v = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(v); setPage(1); }, 300);
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    setImportError('');
    setImportResult(null);
    try {
      const res = await pendingAdmissionAPI.import(file);
      setImportResult(res);
      setPage(1);
      load();
    } catch (err) {
      setImportError(err.response?.data?.error || 'Failed to import the file.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Pending Admissions</h1>
          <p className="text-sm text-slate-400 mt-0.5">{pagination.total.toLocaleString()} record(s) in this view</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleInviteAllClick} disabled={inviteAllChecking || !!inviteAllJobId}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap">
            {inviteAllJobId ? 'Invite Job Running…' : inviteAllChecking ? 'Checking…' : 'Invite All to Pay'}
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileSelected} />
          <button onClick={() => fileInputRef.current?.click()} disabled={importing}
            className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-3 py-2 text-sm font-medium transition-colors">
            {importing ? 'Importing…' : '+ Import Excel List'}
          </button>
        </div>
      </div>

      {inviteAllNotice && (
        <p className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-700">{inviteAllNotice}</p>
      )}

      {importError && <p className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{importError}</p>}
      {importResult && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">
          <p className="font-semibold">{importResult.message}</p>
          {importResult.skipped_duplicates > 0 && <p className="mt-0.5 text-xs">{importResult.skipped_duplicates} row(s) skipped as duplicates.</p>}
          {importResult.errors?.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-xs">
              {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 self-start">
          {[['pending', 'Pending'], ['admitted', 'Admitted'], ['all', 'All']].map(([key, label]) => (
            <button key={key} onClick={() => { setActiveTab(key); setPage(1); }}
              className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
                activeTab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z" />
          </svg>
          <input type="text" placeholder="Search name, petition or firm…" defaultValue={search}
            onChange={handleSearch}
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
        </div>
      </div>

      <div className="flex gap-1 rounded-xl bg-slate-100 p-1 self-start">
        {[['has', 'Has Account'], ['none', "Hasn't Created Account"]].map(([key, label]) => (
          <button key={key} onClick={() => { setAccountTab(key); setPage(1); }}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
              accountTab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading…</div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-1">
            <p className="text-slate-500 font-medium text-sm">No records found</p>
            <p className="text-xs text-slate-400">Import an Excel list or try a different search/filter</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50">
                <tr>
                  <th className="w-12 px-4 py-3" />
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Petition No</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Gender</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Qualification</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Firm</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                  <th className="px-4 py-3 w-32" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${avatarColor(r.full_name)}`}>
                        {(r.full_name || '?')[0].toUpperCase()}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">{r.full_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.petition_no}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.gender || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.qualification || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate">{r.firm_name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{r.phone || '—'}</td>
                    <td className="px-4 py-3">
                      {r.status === 'admitted' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-semibold">
                          Admitted · Roll #{r.roll_no}
                        </span>
                      ) : (
                        <span className="inline-block rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-semibold">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status !== 'admitted' && (
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => setEditTarget(r)}
                            className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap">
                            Edit
                          </button>
                          {r.member_id && !r.has_account && (
                            <button onClick={() => setInviteTarget(r)}
                              className="rounded-lg border border-orange-200 bg-white hover:bg-orange-50 text-orange-700 px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap">
                              Invite to Pay
                            </button>
                          )}
                          {r.member_id && r.has_account && (
                            <span className="rounded-lg bg-emerald-50 text-emerald-700 px-3 py-1.5 text-xs font-medium whitespace-nowrap">
                              ✓ Has Account
                            </span>
                          )}
                          <button onClick={() => setAdmitTarget(r)}
                            className="rounded-lg bg-orange-600 hover:bg-orange-500 text-white px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap">
                            Admit
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && records.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <p className="text-xs text-slate-400">
              Page {pagination.page} of {pagination.total_pages} · {pagination.total.toLocaleString()} total
            </p>
            <div className="flex gap-1.5">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!pagination.has_previous}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                ← Prev
              </button>
              <button onClick={() => setPage((p) => p + 1)} disabled={!pagination.has_next}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {admitTarget && (
        <AdmitModal
          record={admitTarget}
          onClose={() => setAdmitTarget(null)}
          onAdmitted={() => { setAdmitTarget(null); load(); }}
        />
      )}

      {editTarget && (
        <EditPendingAdmissionModal
          record={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {inviteTarget && (
        <InviteToPayModal
          record={inviteTarget}
          onClose={() => setInviteTarget(null)}
        />
      )}

      {inviteAllConfirmOpen && (
        <InviteAllConfirmModal
          eligibleCount={inviteAllEligible}
          onClose={() => setInviteAllConfirmOpen(false)}
          onStarted={(jobId) => { setInviteAllConfirmOpen(false); setInviteAllJobId(jobId); }}
        />
      )}

      {inviteAllJobId && (
        <InviteAllProgressModal
          jobId={inviteAllJobId}
          onClose={() => setInviteAllJobId(null)}
          onFinished={() => load()}
        />
      )}
    </div>
  );
}
